import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  addProduct,
  getDesigners,
  getProducts,
} from "../../api/Design/productApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import { getModelosTipo } from "../../api/Design/modelosTipoApi";
import { getSubgrupos } from "../../api/Subgrupos/subgrupoApi";
import { getMarcas } from "../../api/Marcas/marcaApi";

const emptyNewProduct = () => ({
  tipo_prenda: "",
  ID_Temporada: "",
  ID_SUBGRUPO: "",
  diseñador: "",
  nombre: "",
  ID_MARCA: "",
});

export default function MeetingCreateSampleDialog({
  open,
  onClose,
  onProductChosen,
  mediaCount = 0,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [marcas, setMarcas] = useState([]);
  const [temporadas, setTemporadas] = useState([]);
  const [modelosTipo, setModelosTipo] = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [newProduct, setNewProduct] = useState(emptyNewProduct);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setSelectedProduct(null);
    setNewProduct(emptyNewProduct());
    setTab(0);
    setLoading(true);

    (async () => {
      try {
        const [
          productsData,
          marcasData,
          temporadasData,
          modelosData,
          subgruposData,
          designersData,
        ] = await Promise.all([
          getProducts(),
          getMarcas(),
          getTemporadas(),
          getModelosTipo(),
          getSubgrupos(),
          getDesigners(),
        ]);
        if (cancelled) return;
        setProducts(Array.isArray(productsData) ? productsData : []);
        setMarcas(Array.isArray(marcasData) ? marcasData : []);
        setTemporadas(Array.isArray(temporadasData) ? temporadasData : []);
        setModelosTipo(Array.isArray(modelosData) ? modelosData : []);
        setSubgrupos(Array.isArray(subgruposData) ? subgruposData : []);
        setDesigners(Array.isArray(designersData) ? designersData : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err.message || "Error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Auto-nombre tipo ProductTree
  useEffect(() => {
    if (!open || tab !== 1) return;
    if (
      !newProduct.diseñador ||
      !newProduct.ID_Temporada ||
      !newProduct.tipo_prenda
    ) {
      return;
    }
    const designerAbbr =
      designers.find((d) => d.id === newProduct.diseñador)?.abreviatura ||
      "DES";
    const temporadaAbbr =
      temporadas.find((t) => t.ID_TEMPORADA === newProduct.ID_Temporada)
        ?.ABREVIATURA || "TEMP";
    const modelo = modelosTipo.find(
      (m) => m.ID_TIPO === parseInt(newProduct.tipo_prenda, 10),
    );
    const modeloAbbr = modelo?.ABREVIATURA || "MOD";
    const randomDigits = Math.floor(1000 + Math.random() * 9000);
    const generatedName = `${designerAbbr}${temporadaAbbr}${modeloAbbr}${randomDigits}`;
    if (!newProduct.nombre || newProduct.nombre.startsWith("DES")) {
      setNewProduct((prev) => ({ ...prev, nombre: generatedName }));
    }
  }, [
    open,
    tab,
    newProduct.diseñador,
    newProduct.ID_Temporada,
    newProduct.tipo_prenda,
    newProduct.nombre,
    designers,
    temporadas,
    modelosTipo,
  ]);

  const productOptions = useMemo(
    () =>
      (products || []).map((p) => ({
        ...p,
        label: `${p.nombre || "—"}${p.ID_MARCA ? ` · #${p.id}` : ` · #${p.id}`}`,
      })),
    [products],
  );

  const canConfirmExisting = Boolean(selectedProduct?.id);
  const canConfirmNew =
    Boolean(newProduct.ID_MARCA) &&
    Boolean(newProduct.ID_Temporada) &&
    Boolean(newProduct.tipo_prenda) &&
    Boolean(newProduct.ID_SUBGRUPO) &&
    Boolean(newProduct.diseñador) &&
    Boolean(String(newProduct.nombre || "").trim());

  const handleConfirm = async () => {
    setError("");
    setSaving(true);
    try {
      if (tab === 0) {
        if (!selectedProduct?.id) {
          setError(t("Meetings.createSamplePickProduct"));
          return;
        }
        await onProductChosen?.({
          id: selectedProduct.id,
          ID_MARCA: selectedProduct.ID_MARCA,
          nombre: selectedProduct.nombre,
        });
        onClose?.();
        return;
      }

      const designerAbbr =
        designers.find((d) => d.id === newProduct.diseñador)?.abreviatura ||
        "DES";
      const temporadaAbbr =
        temporadas.find((t) => t.ID_TEMPORADA === newProduct.ID_Temporada)
          ?.ABREVIATURA || "TEMP";
      const modelo = modelosTipo.find(
        (m) => m.ID_TIPO === parseInt(newProduct.tipo_prenda, 10),
      );
      const modeloAbbr = modelo?.ABREVIATURA || "MOD";
      const randomDigits = Math.floor(1000 + Math.random() * 9000);
      const generatedName = `${designerAbbr}${temporadaAbbr}${modeloAbbr}${randomDigits}`;
      const nombreFinal =
        !newProduct.nombre || newProduct.nombre.startsWith("DES")
          ? generatedName
          : newProduct.nombre.trim();

      const payload = {
        tipo_prenda: newProduct.tipo_prenda,
        main_fabric: newProduct.ID_SUBGRUPO,
        diseñador: newProduct.diseñador,
        nombre: nombreFinal,
        ID_Temporadas: newProduct.ID_Temporada,
        ID_MARCA: newProduct.ID_MARCA,
      };

      const res = await addProduct(payload);
      const created = res?.producto || res;
      const productId = created?.id;
      if (!productId) {
        throw new Error(t("Meetings.createSampleProductError"));
      }
      await onProductChosen?.({
        id: productId,
        ID_MARCA: created.ID_MARCA ?? newProduct.ID_MARCA,
        nombre: created.nombre || nombreFinal,
      });
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err.message ||
          t("Meetings.createSampleProductError"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t("Meetings.createSampleProductTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("Meetings.createSampleProductHint", { count: mediaCount })}
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={t("Meetings.createSampleTabExisting")} />
          <Tab label={t("Meetings.createSampleTabNew")} />
        </Tabs>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : tab === 0 ? (
          <Autocomplete
            options={productOptions}
            value={selectedProduct}
            onChange={(_, value) => setSelectedProduct(value)}
            getOptionLabel={(opt) =>
              opt?.label || opt?.nombre || String(opt?.id || "")
            }
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Meetings.createSampleSearchProduct")}
                placeholder={t("Meetings.createSampleSearchPlaceholder")}
              />
            )}
          />
        ) : (
          <Stack spacing={1.75} sx={{ pt: 0.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Meetings.productBrand")}</InputLabel>
              <Select
                label={t("Meetings.productBrand")}
                value={newProduct.ID_MARCA}
                onChange={(e) =>
                  setNewProduct((prev) => ({
                    ...prev,
                    ID_MARCA: e.target.value,
                  }))
                }
              >
                {marcas.map((m) => (
                  <MenuItem key={m.ID_MARCA} value={m.ID_MARCA}>
                    {m.MARCA}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Meetings.productSeason")}</InputLabel>
              <Select
                label={t("Meetings.productSeason")}
                value={newProduct.ID_Temporada}
                onChange={(e) =>
                  setNewProduct((prev) => ({
                    ...prev,
                    ID_Temporada: e.target.value,
                  }))
                }
              >
                {temporadas.map((temp) => (
                  <MenuItem key={temp.ID_TEMPORADA} value={temp.ID_TEMPORADA}>
                    {temp.TEMPORADA}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Meetings.productGarmentType")}</InputLabel>
              <Select
                label={t("Meetings.productGarmentType")}
                value={newProduct.tipo_prenda}
                onChange={(e) =>
                  setNewProduct((prev) => ({
                    ...prev,
                    tipo_prenda: e.target.value,
                  }))
                }
              >
                {modelosTipo.map((m) => (
                  <MenuItem key={m.ID_TIPO} value={m.ID_TIPO}>
                    {m.TIPO}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Meetings.productMainFabric")}</InputLabel>
              <Select
                label={t("Meetings.productMainFabric")}
                value={newProduct.ID_SUBGRUPO}
                onChange={(e) =>
                  setNewProduct((prev) => ({
                    ...prev,
                    ID_SUBGRUPO: e.target.value,
                  }))
                }
              >
                {subgrupos.map((s) => (
                  <MenuItem key={s.ID_SUBGRUPO} value={s.ID_SUBGRUPO}>
                    {s.SUBGRUPO}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Meetings.productDesigner")}</InputLabel>
              <Select
                label={t("Meetings.productDesigner")}
                value={newProduct.diseñador}
                onChange={(e) =>
                  setNewProduct((prev) => ({
                    ...prev,
                    diseñador: e.target.value,
                  }))
                }
              >
                {designers.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.username || d.nombre || d.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label={t("Meetings.productName")}
              value={newProduct.nombre}
              onChange={(e) =>
                setNewProduct((prev) => ({ ...prev, nombre: e.target.value }))
              }
            />
          </Stack>
        )}

        {error ? (
          <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {t("Meetings.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={
            saving ||
            loading ||
            (tab === 0 ? !canConfirmExisting : !canConfirmNew)
          }
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          {t("Meetings.createSampleContinue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
